/**
 * The concierge operator's **ad hoc-add capability token** (KAN-193).
 *
 * A concierge operator (Caroline) browsing a supplier's Shopify storefront
 * directly and pasting a captured product into a salon's cart needs a
 * capability that lets her add an untracked, hand-priced line - something no
 * ordinary customer session can do. That capability is minted server-side and
 * delivered to the storefront as a bearer token, separate from the customer
 * JWT (`localStorage["medusa_auth_token"]`, owned by the vendored Medusa
 * client) and separate from the actor-type dimension
 * (`lib/analytics/actor.ts`) - three different concerns that happen to travel
 * together through the same `/enter` link.
 *
 * Delivered via the operator `/enter` link's `cap` search param (see
 * `pages/enter/EnterPage.tsx`) and stored here so it survives the redirect to
 * `/cart` and every subsequent reload of the session.
 *
 * Sent per-call as the `x-concierge-capability-token` header on the ad hoc
 * add/suggest requests only (see `packages/api-sdk/src/sdk/seller-cart.ts`'s
 * `addAdHocItem`/`suggestAdHocSellers`) - deliberately NOT added to the SDK's
 * `config.globalHeaders`, so it never rides along on a request that has
 * nothing to do with ad hoc items.
 *
 * A shared salon tablet is the expected case elsewhere in this codebase, not
 * an edge case - so clearing this on logout (`pages/logout/LogoutPage.tsx`)
 * is load-bearing: without it, the next person on the browser (who may be the
 * salon herself, not an operator) would inherit Caroline's ad hoc-add
 * capability.
 *
 * Mirrors `lib/analytics/actor.ts`'s shape: plain, try/catch-guarded
 * `localStorage` calls, tolerant of storage being unavailable (Safari private
 * mode, embedded webviews) so a missing/blocked store never breaks the page -
 * it just means the ad hoc paste-in entry point stays gated off
 * (`useIsConciergeOperator`) and the add call fails fast with a clear error
 * (`useAddAdHocSellerCartItem`) instead of silently sending no token.
 */

const CAPABILITY_TOKEN_STORAGE_KEY = "ss_concierge_capability_token";

/**
 * Read the stored capability token, or `null` if none is stored (including
 * when storage itself is unavailable).
 *
 * Callers that need to send it as a header should treat `null` as "this
 * session cannot perform the ad hoc add" - never fall back to an empty
 * string or omit the check.
 */
export function getCapabilityToken(): string | null {
  try {
    return window.localStorage.getItem(CAPABILITY_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Persist the capability token a `/enter` redemption's `cap` param carried.
 *
 * Only operator links carry `cap` - a salon-facing link never does - so this
 * is called conditionally, after the redemption's customer JWT and actor type
 * are already stored (see `EnterPage.tsx`).
 */
export function setCapabilityToken(token: string): void {
  try {
    window.localStorage.setItem(CAPABILITY_TOKEN_STORAGE_KEY, token);
  } catch {
    // Storage unavailable - the operator can still finish this page load's
    // session, but the token won't survive a reload and any ad hoc add will
    // fail fast rather than silently succeed without a token.
  }
}

/**
 * Forget the stored capability token. Called on logout alongside
 * `endSession()` so the next person on this browser - the expected case is a
 * shared salon tablet - does not inherit the previous operator's ad hoc-add
 * capability.
 */
export function clearCapabilityToken(): void {
  try {
    window.localStorage.removeItem(CAPABILITY_TOKEN_STORAGE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
